import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { getPartnerOrAdvocateProfileInformation, fetchFeatureFlag } from '@atlassian/temp-proxies';
import { UUID_REGEX, ENABLE_DESIGN_ITERATION_1, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { ApplicationContext, UnderMaintenancePage } from '@css-xp/base';
import { usePartnerRedirectHandler } from '@css-xp/ppc-custom-hooks';
import type { PartnerProfile } from '@css-xp/ppc-types';
import { handlePPCLogin } from '@css-xp/ppc-utils';
import { AppLoader } from '@css-xp/ppc-page-loaders';

export interface PartnerContextData {
  userProfile: PartnerProfile | null;
  loading: boolean;
}

export const PartnerApplicationContext = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const matchedTransactionAccountId = location?.pathname?.match(UUID_REGEX)?.[0];
  const [isPPCEnabled, setIsPPCEnabled] = useState(false);
  const [partnerContextData, setPartnerContextData] = useState<PartnerContextData>({
    userProfile: null,
    loading: true,
  });
  const [isRedirectionComplete, setIsRedirectionComplete] = useState(false);
  const [handleRedirectionForPPC] = usePartnerRedirectHandler(partnerContextData.userProfile);
  const [isFetchingDesignIterationFeatureFlag, setIsFetchingDesignIterationFeatureFlag] = useState(true);
  const [isFetchingEnablePPCAccess, setIsFetchingEnablePPCAccess] = useState(true);
  const [showNewAppLoader, setShowNewAppLoader] = useState(false);

  const fetchContextData = useCallback(async () => {
    try {
      const userProfileData = await getPartnerOrAdvocateProfileInformation(matchedTransactionAccountId);
      setPartnerContextData({
        userProfile: userProfileData,
        loading: false,
      });
    } catch (e: any) {
      if (e.statusCode === 401) {
        handlePPCLogin();
      } else {
        setPartnerContextData((prevValue) => {
          return { ...prevValue, loading: false };
        });
      }
    }
  }, []);

  const fetchDesignIterationFlag = useCallback(async () => {
    try {
      const res = await fetchFeatureFlag(ENABLE_DESIGN_ITERATION_1);
      setShowNewAppLoader(res.enabled || false);
    } catch (e) {
      setShowNewAppLoader(false);
    }

    setIsFetchingDesignIterationFeatureFlag(false);
  }, []);

  const fetchEnablePPCAccess = useCallback(async () => {
    try {
      const res = await fetchFeatureFlag(STATSIG_FEATURE_GATES.ENABLE_PPC_ACCESS);
      setIsPPCEnabled(res.enabled || false);
    } catch (e) {
      setIsPPCEnabled(false);
    }

    setIsFetchingEnablePPCAccess(false);
  }, []);

  useEffect(() => {
    fetchDesignIterationFlag();
    fetchEnablePPCAccess();
    fetchContextData();
  }, [fetchContextData, fetchDesignIterationFlag]);

  useEffect(() => {
    if (!partnerContextData.loading && !isFetchingDesignIterationFeatureFlag && !isFetchingEnablePPCAccess) {
      isPPCEnabled && handleRedirectionForPPC();
      setIsRedirectionComplete(true);
    }
  }, [
    handleRedirectionForPPC,
    partnerContextData.loading,
    isFetchingDesignIterationFeatureFlag,
    isFetchingEnablePPCAccess,
    isPPCEnabled,
  ]);

  if (isFetchingDesignIterationFeatureFlag || isFetchingEnablePPCAccess) return null;

  if (partnerContextData.loading || !isRedirectionComplete) {
    return showNewAppLoader ? <AppLoader /> : <LoadingIndicator />;
  }

  if (!isPPCEnabled) {
    return <UnderMaintenancePage appName={'Partner Purchasing Center'} />;
  }

  return (
    <ApplicationContext.Provider
      value={{
        ...partnerContextData.userProfile,
        product: 'PPC',
        showNewAppLoader,
      }}
    >
      {children}
    </ApplicationContext.Provider>
  );
};
